<template>
  <v-card>
    <v-card-text class="d-flex pb-0">
      <div
        class="mr-5"
        style="width:350px"
      >
        <SelectStock
          @symbol-changed="symbol = $event"
        />
      </div>
      <div
        class="mr-5"
        style="width:110px"
      >
        <v-select
          v-model="resolution"
          :items="resolutionOptions"
          dense
          outlined
          hide-details
        />
      </div>
      <v-select
        v-model="selectedCharts"
        :items="chartOptionsWithPremium"
        multiple
        dense
        outlined
        hide-details
        style="max-width: 425px; flex: 0 0 auto;"
        :menu-props="{ maxHeight: 300, closeOnClick: false }"
      >
        <template v-slot:selection="{ item }">
          <v-chip
            small
            class="ma-1"
          >
            {{ item.text }}
            <v-icon
              v-if="item.locked"
              x-small
              color="grey"
              class="ml-1"
            >
              mdi-lock
            </v-icon>
          </v-chip>
        </template>
        <template v-slot:item="{ item, attrs }">
          <v-list-item
            v-bind="attrs"
            @click.prevent="handleChartOptionClick(item)"
          >
            <v-list-item-action>
              <v-checkbox
                :input-value="selectedCharts.includes(item.value)"
                :disabled="item.locked"
                @click.stop
              />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.text }}
                <v-icon
                  v-if="item.locked"
                  small
                  color="grey"
                  class="ml-2"
                >
                  mdi-lock
                </v-icon>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-select>
    </v-card-text>

    <v-card-text class="pa-4 pb-6">
      <TradingChart
        :key="chartKey"
        ref="chart"
        :symbol="symbol"
        :resolution="resolution"
        :chart-config="selectedCharts"
        @timeRangeChange="timeRangeChange"
      />
    </v-card-text>

    <!-- Upgrade Dialog -->
    <v-dialog
      v-model="showUpgradeDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          Nâng cấp tài khoản
        </v-card-title>
        <v-card-text>
          Vui lòng nâng cấp tài khoản để được cấp quyền xem
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showUpgradeDialog = false"
          >
            Đóng
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>

import { EventBus } from '@/event-bus'
import TradingChart from '../components/TradingChart.vue'
import SelectStock from '../components/SelectStock.vue'
import { TRADING_HISTORY_API, TRADING_REALTIME_URL } from '../constants'
import {
  aggregateWeekly, barsFromHistory, emptyBars, parseTick, weekStart,
} from '../utils/rawData'
import { feedClient } from '../utils/feedClient'

const feed = feedClient(TRADING_REALTIME_URL)

const fetchRange = 500 * 24 * 3600

const featureMap = {
  candle: null,
  money: 'trading-chart',
  diff: 'trading-chart',
  adwin: 'adwin-chart',
  volume: null,
}

export default {
  components: {
    SelectStock,
    TradingChart,
  },
  data() {
    return {
      symbol: null,
      // 'loading' | 'ready' | 'empty' | 'error', for the first window of the current choice
      status: 'loading',
      // Where the oldest window held starts, whether the one before it is on its way,
      // and whether there is none before it
      oldest: 0,
      loadingOlder: false,
      ended: false,
      resolution: '1D',
      resolutionOptions: [
        { text: '5 phút', value: '5' },
        { text: '15 phút', value: '15' },
        { text: '30 phút', value: '30' },
        { text: '1 giờ', value: '60' },
        { text: '1 ngày', value: '1D' },
        { text: '1 tuần', value: '1W' },
      ],
      selectedCharts: [],
      chartKey: 0,
      cachedRawData: null,
      showUpgradeDialog: false,
    }
  },
  computed: {
    user() {
      return this.$store.getters['user/user'] || {}
    },
    premiumFeatures() {
      return this.user.premium_features || []
    },
    isWeekly() {
      return this.resolution === '1W'
    },
    chartConfigOptions() {
      const options = {
        money: [
          { text: 'Giá', value: 'candle' },
          { text: 'Dòng tiền', value: 'money' },
          { text: 'Chênh lệch', value: 'diff' },
          { text: 'Khối lượng', value: 'volume' },
        ],
        adwin: [
          { text: 'Giá', value: 'candle' },
          { text: 'Đỉnh-Đáy', value: 'adwin' },
          { text: 'Khối lượng', value: 'volume' },
        ],
      }

      return options[this.$route.meta.variant] || options.money
    },
    chartOptionsWithPremium() {
      return this.chartConfigOptions.map(option => {
        const requiredFeature = featureMap[option.value]
        const isLocked = requiredFeature && !this.premiumFeatures.includes(requiredFeature)

        return {
          ...option,
          locked: isLocked,
        }
      })
    },
  },
  watch: {
    symbol() {
      this.subscribeLive()
      this.reloadData()
    },
    resolution() {
      this.reloadData()
    },
  },
  created() {
    // Every reload takes a new token: an answer that belongs to an earlier choice is dropped
    this.token = 0
    this.unsubscribe = null
    this.selectedCharts = this.chartConfigOptions.map(o => o.value)
    this.reloadData()
  },
  beforeDestroy() {
    this.stopLive()
  },
  methods: {
    timeRangeChange({ from }) {
      if (from <= 80) this.loadOlder()
    },
    // Weekly bars are built from daily ones, so pages must not split a week (cut on Sunday noon UTC)
    rangeStart(from) {
      return this.isWeekly ? weekStart(from) - 12 * 3600 : from
    },
    // The window of history that ends at `to`, or null when it would start before 0
    historyWindow(to) {
      const from = this.rangeStart(to - fetchRange)

      return from < 0 ? null : { from, to }
    },
    // The bars of one window, null when the feed has none there
    requestBars({ symbol, resolution }, { from, to }) {
      const weekly = resolution === '1W'
      const params = {
        symbol, resolution: weekly ? '1D' : resolution, from, to,
      }

      return this.$http.get(TRADING_HISTORY_API, { params, timeout: 15000 })
        .then(resp => {
          const bars = barsFromHistory(resp.data)

          return bars && weekly ? aggregateWeekly(bars) : bars
        })
    },
    reloadData() {
      if (!this.symbol) return

      this.token += 1
      const { token } = this
      const choice = { symbol: this.symbol, resolution: this.resolution }
      const first = this.historyWindow(Math.floor(Date.now() / 1000))
      this.oldest = first.from
      this.loadingOlder = false
      this.ended = false
      this.status = 'loading'

      this.requestBars(choice, first)
        .then(bars => {
          if (token !== this.token) return
          this.status = bars ? 'ready' : 'empty'
          this.showBars(bars || emptyBars(), true)
        })
        .catch(e => {
          if (token !== this.token) return
          console.error(e)
          this.status = 'error'
          // The previous choice's bars must not stay on screen under this one's name
          this.showBars(emptyBars(), true)
          EventBus.$emit('show-error', 'Không tải được dữ liệu giá.')
        })
    },
    loadOlder() {
      if (this.status !== 'ready' || this.loadingOlder || this.ended) return

      const before = this.historyWindow(this.oldest)
      if (!before) {
        this.ended = true

        return
      }

      const { token } = this
      this.loadingOlder = true
      this.requestBars({ symbol: this.symbol, resolution: this.resolution }, before)
        .then(older => {
          if (token !== this.token) return
          this.loadingOlder = false
          if (!older) {
            this.ended = true

            return
          }
          // Moved back only once the window has arrived, so no window is skipped
          this.oldest = before.from
          this.showBars(older, false)
        })
        .catch(e => {
          if (token !== this.token) return
          // A failed window ends nothing: the next time the view reaches the start asks again
          this.loadingOlder = false
          console.error(e)
        })
    },
    showBars(bars, reset) {
      this.cachedRawData = bars
      if (this.$refs.chart) this.$refs.chart.setRawData(bars, reset)
    },
    // The symbol's room: the old one is left before the next is joined, and the last when the
    // page is left. After a reconnect the history is loaded again, which brings the bars missed
    // in between; a tick alone would start a bar at the present moment and leave the gap.
    subscribeLive() {
      this.stopLive()
      if (this.symbol) this.unsubscribe = feed.subscribe(`CHART.${this.symbol}`, this.onTick, this.reloadData)
    },
    stopLive() {
      if (this.unsubscribe) this.unsubscribe()
      this.unsubscribe = null
    },
    onTick(data) {
      const tick = parseTick(data)
      // Another symbol's tick, or one that comes before this choice's history
      if (!tick || tick.symbol !== this.symbol || this.status !== 'ready') return
      if (this.$refs.chart) this.$refs.chart.pushData(data)
    },
    handleChartOptionClick(item) {
      if (item.locked) {
        this.showUpgradeDialog = true

        return
      }

      const index = this.selectedCharts.indexOf(item.value)
      if (index === -1) {
        this.selectedCharts.push(item.value)
      } else {
        this.selectedCharts.splice(index, 1)
      }

      this.onChartConfigChange()
    },
    onChartConfigChange() {
      if (this.$refs.chart && this.$refs.chart.rawData) {
        this.cachedRawData = this.$refs.chart.rawData
      }

      this.chartKey += 1

      this.$nextTick(() => {
        if (this.cachedRawData && this.$refs.chart) {
          this.$refs.chart.setRawData(this.cachedRawData, true)
        }
      })
    },
  },
}

</script>
